import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  StyledButton,
  StyledTypography,
  StyledTypographyBlack,
  StyledTextField,
} from "../styles/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBatteryConfig } from "../context/BatteryConfigContext";
import CapacityVsCyclePlot from "./CapacityVsCyclePlot";
import IReVsCyclePlot from "./IReVsCyclePlot";
import Papa from "papaparse";
import { yellow } from "@mui/material/colors";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const fetchData = async (file) => {
  const response = await axios.get(`${process.env.BACKEND_URL}files/${file}`, {
    responseType: "blob",
  });

  return new Promise((resolve, reject) => {
    Papa.parse(response.data, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        resolve(data);
      },
      error: (error) => reject(error),
    });
  });
};

const processData = (data, cellCapacity, calcFunc) => {
  const filteredData = data.filter((row) => parseFloat(row[1]) !== 0);
  const x = filteredData.map((row) => parseFloat(row[0]));
  const y = filteredData.map((row) => calcFunc(row[1], cellCapacity));

  return {
    x,
    y,
    xm: Math.min(...x) - 1.5,
    xM: Math.max(...x) + 1.5,
    ym: Math.min(...y) - 0.1,
    yM: Math.max(...y) + 0.1,
  };
};

const calcCapacity = (val, capacity) => parseFloat(val) * capacity;
const calcIR = (val, capacity) => 1 / (parseFloat(val) * capacity);

const BatteryLifetime2 = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [capacityProgress, setCapacityProgress] = useState(0);
  const [energyThroughputVal, setEnergyThroughputVal] = useState(0);
  const plotRef = useRef(null);
  const [plotFrameindex, setPlotFrameIndex] = useState(0);
  const [capacityData, setCapacityData] = useState({ x: [], y: [], xm: -1, xM: 1, ym: -1, yM: 1 });
  const [irData, setIrData] = useState({ x: [], y: [], xm: -1, xM: 1, ym: -1, yM: 1 });
  const [pricePerKwh, setPricePerKwh] = useState(0.12);
  const [originalAssetValue, setOriginalAssetValue] = useState(3);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const { selectedConfig } = useBatteryConfig();
  const navigate = useNavigate();

  const handleNavigateToCertificate = () => {
    navigate("/batteryscope/certificate");
  };


  useEffect(() => {
    if (!selectedConfig || !selectedConfig.cellCapacity) {
      return;
    }
    const loadData = async () => {
      try {
        const csvData = await fetchData("cyclecsv4000-2024-07-19-08-38-05.csv");
        setCapacityData(processData(csvData, selectedConfig.cellCapacity, calcCapacity));
        setIrData(processData(csvData, selectedConfig.cellCapacity, calcIR));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching or processing data:", error);
        setError("Error fetching or processing data");
      }
    };

    loadData();
  }, [selectedConfig]);

  const handlePlayPause = () => setIsPlaying(!isPlaying);
  const handleReset = () => {
    clearInterval(plotRef.current);
    setPlotFrameIndex(0);
    setIsPlaying(false);
    setCapacityProgress(0);
  };

  useEffect(() => {
    if (isPlaying && irData.x.length > 0 && capacityData.x.length > 0) {
      plotRef.current = setInterval(() => {
        setPlotFrameIndex((prev) => (prev + 50) % irData.x.length);
      }, 1);
    } else {
      clearInterval(plotRef.current);
    }

    return () => clearInterval(plotRef.current);
  }, [isPlaying, irData.x.length, capacityData.x.length]);


  useEffect(() => {
    if (selectedConfig && selectedConfig.cellCapacity) {
    setCapacityProgress((capacityData.y[plotFrameindex] / selectedConfig.cellCapacity) * 100);
    setEnergyThroughputVal(
      capacityData.y.slice(0, plotFrameindex).reduce((acc, curr) => acc + curr, 0),
    );
    }
  }, [plotFrameindex, capacityData, selectedConfig]);


  const handleNavigateToSubmit = () => {
    navigate("/batteryscope/submit");
  };


  const handlePriceChange = (event) => {
    setPricePerKwh(parseFloat(event.target.value) || 0);
  };

  const handleOriginalAssetValueChange = (event) => {
    setOriginalAssetValue(parseFloat(event.target.value) || 0);
  };

  return (
    <div style={{ height: "calc(100vh - 100px)", overflowY: "scroll", padding: "10px" }}>
      <div className="visualize-data" style={{ textAlign: "center", padding: 0 }}>
        {(!selectedConfig || !selectedConfig.cellCapacity) ? (
          <div className="mt-4">
            <StyledTypography variant="h6" sx={{ color: "#e8442d" }}>
              No Battery Data Selected
            </StyledTypography>
            <Typography variant="body1" gutterBottom className="mt-2 mb-4">
              To visualize battery data, please submit your battery configuration first.
            </Typography>
            <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
              Go to Submit Page
            </StyledButton>
          </div>
        ) : loading ? (
          <CircularProgress />
        ) : (
          <>
            {error ? (
              <>
                <StyledTypography variant="body1" color="error" gutterBottom>
                  {error}
                </StyledTypography>
                <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                  Go to Submit Page
                </StyledButton>
              </>
            ) : (
              <>
                <div className="flex flex-row space-between gap-2 w-[100%]">
                  <div className="flex flex-col gap-2 w-[60%]">
                    <IReVsCyclePlot
                      data={irData}
                      frameIndex={plotFrameindex}
                    />
                    <CapacityVsCyclePlot
                      data={capacityData}
                      frameIndex={plotFrameindex}
                    />
                     <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "20px",
                        justifyContent: "center",
                      }}
                    >
                      <StyledButton
                        variant="contained"
                        color={isPlaying ? "secondary" : "primary"}
                        onClick={handlePlayPause}
                      >
                        {isPlaying ? "Pause" : "Play"}
                      </StyledButton>
                      <StyledButton variant="contained" color="warning" onClick={handleReset}>
                        Reset
                      </StyledButton>
                    </Box>
                  </div>
                  <div style={{ width: "40%" }} className="flex flex-col justify-center">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        padding: "20px",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          border: `2px solid green`,
                          padding: "10px",
                          borderRadius: "8px",
                          width: "100%",
                        }}
                      >
                        <StyledTypography
                          variant="h6"
                          sx={{
                            color: "green",
                            mb: 2,
                          }}
                        >
                          Cell Capacity
                        </StyledTypography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                          <LinearProgress
                            variant="determinate"
                            value={capacityProgress}
                            sx={{ height: 20, backgroundColor: "lightgrey" }}
                          />
                          <StyledTypography variant="body1">
                            {parseFloat((capacityProgress * selectedConfig.cellCapacity
                            ) / 100).toFixed(2)} Ah
                          </StyledTypography>
                          <LinearProgress
                            variant="determinate"
                            value={capacityProgress}
                            sx={{ height: 20, backgroundColor: "lightgrey" }}
                          />
                          <StyledTypography variant="body1">
                            {parseFloat(((capacityProgress * selectedConfig.cellCapacity) / 100) * 3.6).toFixed(2)} Kwh
                          </StyledTypography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          border: `2px solid blue`,
                          padding: "10px",
                          borderRadius: "8px",
                          width: "100%",
                        }}
                      >
                        <StyledTypography
                          variant="h6"
                          sx={{
                            color: "blue",
                            mb: 2,
                          }}
                        >
                          Energy Throughput
                        </StyledTypography>
                        <LinearProgress
                          variant="determinate"
                          value={(energyThroughputVal / (4000 * selectedConfig.cellCapacity)) * 100}
                          sx={{ height: 20, backgroundColor: "lightgrey" }}
                        />
                        <StyledTypography variant="body1">
                          {parseFloat(energyThroughputVal).toFixed(2)} Kwh
                        </StyledTypography>
                      </Box>
                      <Box
                        sx={{
                          border: `2px solid orange`,
                          padding: "10px",
                          borderRadius: "8px",
                          width: "100%",
                        }}
                      >
                        <StyledTypography
                          variant="h6"
                          sx={{
                            color: "orange",
                            mb: 2,
                          }}
                        >
                          Revenue Potential
                        </StyledTypography>

                        <LinearProgress
                          variant="determinate"
                          value={((4000 * selectedConfig.cellCapacity - energyThroughputVal) / (4000 * selectedConfig.cellCapacity)) * 100}
                          sx={{ height: 20, backgroundColor: "lightgrey" }}
                        />

                        <StyledTypography variant="body1">
                          {parseFloat(4000 * selectedConfig.cellCapacity - energyThroughputVal).toFixed(2)} Kwh
                        </StyledTypography>

                        <StyledTextField
                          variant="outlined"
                          type="number"
                          value={pricePerKwh}
                          onChange={handlePriceChange}
                          sx={{ mt: 2, width: "100%", color: "white" }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <StyledTypographyBlack sx={{ fontWeight: "700" }}>
                                  $/kWh
                                </StyledTypographyBlack>
                              </InputAdornment>
                            ),
                            inputProps: {
                              min: 0,
                              max: 1000,
                              style: {
                                fontWeight: "700",
                                fontFamily: "Bai Jamjuree",
                              },
                            },
                          }}
                        />

                        <StyledTypography variant="body1" sx={{ mt: 2 }}>
                          Estimated Revenue Potential: $
                          {parseFloat((4000 * selectedConfig.cellCapacity - energyThroughputVal) * pricePerKwh).toFixed(2)}
                        </StyledTypography>
                      </Box>
                      <Box
                        sx={{
                          border: `2px solid yellow`,
                          padding: "10px",
                          borderRadius: "8px",
                          width: "100%",
                        }}
                      >
                        <StyledTypography
                          variant="h6"
                          sx={{
                            color: "yellow",
                            mb: 2,
                          }}
                        >
                          Asset Value
                        </StyledTypography>
                        <LinearProgress
                          variant="determinate"
                          value={capacityProgress}
                          sx={{ height: 20, backgroundColor: "lightgrey" }}
                        />

                        <StyledTypography variant="body1">
                          $ {parseFloat((originalAssetValue * capacityProgress)/5).toFixed(2)} 
                        </StyledTypography>

                        <StyledTextField
                          variant="outlined"
                          type="number"
                          value={originalAssetValue}
                          onChange={handleOriginalAssetValueChange}
                          sx={{ mt: 2, width: "100%", color: "white" }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <StyledTypographyBlack sx={{ fontWeight: "700" }}>
                                  $
                                </StyledTypographyBlack>
                              </InputAdornment>
                            ),
                            inputProps: {
                              min: 0,
                              max: 1000,
                              style: {
                                fontWeight: "700",
                                fontFamily: "Bai Jamjuree",
                              },
                            },
                          }}
                        />

                        <StyledTypography variant="body1" sx={{ mt: 2 }}>
                          Estimated Asset Value: $
                          {parseFloat((originalAssetValue * capacityProgress)/ selectedConfig.cellCapacity).toFixed(2)}
                        </StyledTypography>
                      </Box>
                    </Box>{" "}
                  </div>
                </div>
                <StyledButton variant="contained" color="primary" onClick={handleNavigateToCertificate} sx={{ mt: 2, mb: 2 }}>
                  Generate Certificate
                  <ArrowForwardIcon />
                </StyledButton>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BatteryLifetime2;
